import { validatePhoneForCountry } from '@/lib/constants/phoneValidation';

/**
 * Custom registration questions an event type (webinar or appointment) asks
 * on its public booking form, on top of the fixed Name / Email / Phone.
 * Each event type stores its own list in `event_types.custom_questions`, so
 * every webinar can ask different things.
 *
 * Mirrors backend/src/domain/scheduling/customQuestions.js — keep the type list
 * in sync. Answers are stored as plain strings (checkbox picks joined with
 * ", ") so existing readers of `bookings.answers` keep working.
 */

export type CustomQuestionType =
    | 'text' | 'textarea' | 'number' | 'email' | 'phone' | 'date'
    | 'select' | 'radio' | 'checkbox';

export interface CustomQuestion {
    id: string;
    question: string;
    type: CustomQuestionType;
    required: boolean;
    options?: string[];
    placeholder?: string;
}

export interface CustomAnswer {
    question_id?: string;
    question: string;
    answer: string;
}

/** Form value per question: a string, or the ticked options of a checkbox question. */
export type CustomAnswerValue = string | string[];

export const QUESTION_TYPE_OPTIONS: { value: CustomQuestionType; label: string }[] = [
    { value: 'text', label: 'Short text' },
    { value: 'textarea', label: 'Long text' },
    { value: 'number', label: 'Number' },
    { value: 'email', label: 'Email' },
    { value: 'phone', label: 'Phone number' },
    { value: 'date', label: 'Date' },
    { value: 'select', label: 'Dropdown (choose one)' },
    { value: 'radio', label: 'Radio buttons (choose one)' },
    { value: 'checkbox', label: 'Checkboxes (choose many)' },
];

/** Fields every booking form always asks, required — shown locked in the editor. */
export const DEFAULT_BOOKING_FIELDS: { label: string; typeLabel: string }[] = [
    { label: 'Your name', typeLabel: 'Short text' },
    { label: 'Email address', typeLabel: 'Email' },
    { label: 'Phone number', typeLabel: 'Phone number' },
];

const TYPES = QUESTION_TYPE_OPTIONS.map((t) => t.value);

export const typeHasOptions = (type: CustomQuestionType): boolean =>
    type === 'select' || type === 'radio' || type === 'checkbox';

export const MAX_CUSTOM_QUESTIONS = 30;

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const newCustomQuestion = (type: CustomQuestionType = 'text'): CustomQuestion => ({
    id: `q_${Date.now().toString(36)}${Math.random().toString(36).slice(2, 7)}`,
    question: '',
    type,
    required: false,
    ...(typeHasOptions(type) ? { options: ['', ''] } : {}),
});

/** Read a stored question list (array or JSON string) into a safe shape. */
export const normalizeCustomQuestions = (raw: unknown): CustomQuestion[] => {
    let list: unknown = raw;
    if (typeof list === 'string') {
        try { list = JSON.parse(list); } catch { return []; }
    }
    if (!Array.isArray(list)) return [];
    return list
        .filter((q): q is Record<string, any> => !!q && typeof q === 'object')
        .map((q, i) => {
            const type: CustomQuestionType = TYPES.includes(q.type) ? q.type : 'text';
            return {
                id: typeof q.id === 'string' && q.id ? q.id : `q_${i + 1}`,
                question: typeof q.question === 'string' ? q.question : '',
                type,
                required: q.required === true,
                ...(typeHasOptions(type)
                    ? { options: Array.isArray(q.options) ? q.options.filter((o: unknown) => typeof o === 'string') : [] }
                    : {}),
                ...(typeof q.placeholder === 'string' && q.placeholder ? { placeholder: q.placeholder } : {}),
            };
        })
        .filter((q) => q.question.trim() !== '');
};

/** Host-side check before saving. Returns an error message, or null when valid. */
export const validateCustomQuestions = (questions: CustomQuestion[]): string | null => {
    for (let i = 0; i < questions.length; i++) {
        const q = questions[i];
        const n = i + 1;
        if (!q.question.trim()) return `Question ${n} needs a label.`;
        if (typeHasOptions(q.type)) {
            const options = (q.options || []).map((o) => o.trim()).filter(Boolean);
            if (options.length === 0) return `"${q.question.trim()}" needs at least one option.`;
            if (new Set(options.map((o) => o.toLowerCase())).size !== options.length) {
                return `"${q.question.trim()}" has the same option twice.`;
            }
        }
    }
    return null;
};

/** Trim labels/options and drop blank options before sending to the API. */
export const cleanCustomQuestionsForSave = (questions: CustomQuestion[]): CustomQuestion[] =>
    questions.map((q) => {
        const base: CustomQuestion = { id: q.id, question: q.question.trim(), type: q.type, required: q.required };
        if (typeHasOptions(q.type)) {
            return { ...base, options: (q.options || []).map((o) => o.trim()).filter(Boolean) };
        }
        const placeholder = q.placeholder?.trim();
        return placeholder ? { ...base, placeholder } : base;
    });

const isEmpty = (value: CustomAnswerValue | undefined): boolean =>
    Array.isArray(value) ? value.length === 0 : !(value || '').trim();

/** Invitee-side check for one answer. Returns an error message, or '' when valid. */
export const validateCustomAnswer = (
    q: CustomQuestion,
    value: CustomAnswerValue | undefined,
    phoneCode = '+91'
): string => {
    if (isEmpty(value)) return q.required ? 'This field is required' : '';
    const text = Array.isArray(value) ? '' : (value || '').trim();
    switch (q.type) {
        case 'number':
            return Number.isFinite(Number(text)) ? '' : 'Enter a valid number';
        case 'email':
            return EMAIL_RE.test(text) ? '' : 'Enter a valid email';
        case 'phone':
            return validatePhoneForCountry(phoneCode, text);
        case 'date':
            return Number.isNaN(new Date(text).getTime()) ? 'Enter a valid date' : '';
        default:
            return '';
    }
};

/** The stored string for one answer. */
export const formatCustomAnswer = (
    q: CustomQuestion,
    value: CustomAnswerValue | undefined,
    phoneCode = '+91'
): string => {
    if (Array.isArray(value)) {
        // Keep the host's option order, not the click order.
        const picked = new Set(value);
        return (q.options || []).filter((o) => picked.has(o)).join(', ');
    }
    const text = (value || '').trim();
    if (q.type === 'phone' && text) return `${phoneCode} ${text.replace(/\D/g, '')}`;
    return text;
};

/** Read stored booking answers (array or JSON string) into a safe shape. */
export const normalizeBookingAnswers = (raw: unknown): CustomAnswer[] => {
    let list: unknown = raw;
    if (typeof list === 'string') {
        try { list = JSON.parse(list); } catch { return []; }
    }
    if (!Array.isArray(list)) return [];
    return list
        .filter((a): a is Record<string, any> => !!a && typeof a === 'object')
        .map((a) => ({
            ...(typeof a.question_id === 'string' && a.question_id ? { question_id: a.question_id } : {}),
            question: typeof a.question === 'string' ? a.question : '',
            answer: a.answer === null || a.answer === undefined ? '' : String(a.answer),
        }));
};

/** One answer column in the registrants list / export. */
export interface AnswerColumn {
    label: string;
    questionId?: string;
}

/**
 * Columns for the event's current questions, in the host's order, followed by
 * one column per label that only older registrations answered (a question the
 * host since deleted) — so removing a question never hides collected answers.
 */
export const answerColumnsFor = (
    questions: CustomQuestion[],
    rows: { answers?: unknown }[]
): AnswerColumn[] => {
    const columns: AnswerColumn[] = questions.map((q) => ({ label: q.question, questionId: q.id }));
    const ids = new Set(questions.map((q) => q.id));
    const labels = new Set(questions.map((q) => q.question));
    for (const row of rows) {
        for (const a of normalizeBookingAnswers(row.answers)) {
            if (a.question_id && ids.has(a.question_id)) continue;
            if (!a.question || labels.has(a.question)) continue;
            labels.add(a.question);
            columns.push({ label: a.question });
        }
    }
    return columns;
};

/** The answer a registration gave for one column ('' when unanswered). */
export const answerFor = (rawAnswers: unknown, column: AnswerColumn): string => {
    const answers = normalizeBookingAnswers(rawAnswers);
    const byId = column.questionId ? answers.find((a) => a.question_id === column.questionId) : undefined;
    if (byId) return byId.answer;
    // Answers saved before question ids were stored (or to a deleted question
    // with the same label) match on the label.
    const byLabel = answers.find((a) => a.question === column.label);
    return byLabel ? byLabel.answer : '';
};

/** Answers payload for the booking API — unanswered optional questions are left out. */
export const buildCustomAnswers = (
    questions: CustomQuestion[],
    values: Record<string, CustomAnswerValue>,
    phoneCodes: Record<string, string> = {}
): CustomAnswer[] =>
    questions
        .map((q) => ({
            question_id: q.id,
            question: q.question,
            answer: formatCustomAnswer(q, values[q.id], phoneCodes[q.id]),
        }))
        .filter((a) => a.answer !== '');
